/**
 * PostProcessing — the render paths, the tier budgets, and the governor hooks.
 *
 * Three render paths, chosen by tier and never rebuilt at runtime:
 *
 *   pipeline  ultra/high: toon outline pass → bloom → tone map → FXAA → grade
 *   glow      medium:     scene pass → half-resolution bloom → tone map → grade
 *   direct    low:        renderer.render(), no post at all
 *
 * ── HDR, and what the bloom is for ─────────────────────────────────────────
 *
 * The scene pass renders to a half-float target and the bloom thresholds on
 * LINEAR HDR luminance before the tone map. The convention every VFX module
 * follows: anything meant to glow is emitted at ≥ 3× a normalised colour
 * (`fx.js: hot()`), and the threshold sits ABOVE anything the arena or a body
 * reaches under the key light. So the bloom catches sparks, cores, rings and
 * tracers, and leaves the deck and the characters alone. The old 0.55
 * threshold did the opposite: the toon highlights bloomed and the VFX did not.
 *
 * ── Medium gets a post pass now ────────────────────────────────────────────
 *
 * Medium is where every phone lands, and it had no bloom — the defining cue of
 * the art style was missing on the device the game is for. The `glow` path is
 * a separate, cheaper chain rather than the full one with parts switched off:
 * no outline pass (hulls draw the silhouette on the geometry), no FXAA, and a
 * bloom whose mip chain starts at a quarter of the frame. Built once at boot.
 * If frames suffer the governor drops to `low`, which has no post pass.
 *
 * ── Grade ──────────────────────────────────────────────────────────────────
 *
 * A vignette, a touch of saturation, and a tint in the SHADOWS ONLY toward a
 * navy-teal — the Trading Floor palette is gold on near-black, and the tint
 * lives where the black is without ever colouring a highlight. Display-referred,
 * after the tone map, so a highlight is never pushed back into the bloom.
 *
 * ── Two crash lessons from v1, preserved ───────────────────────────────────
 *   - every pipeline is built ONCE at boot and never rebuilt
 *   - shadow rendering is suspended without disposing compiled shadow nodes
 */

import * as THREE from 'three/webgpu';
import { toonOutlinePass, pass, renderOutput, rtt, uniform, mix, vec3, vec4, float, uv, smoothstep, luminance, oneMinus } from 'three/tsl';
import BloomNode from 'three/addons/tsl/display/BloomNode.js';
import { gaussianBlur } from 'three/addons/tsl/display/GaussianBlurNode.js';
import { fxaa } from 'three/addons/tsl/display/FXAANode.js';

import { QualityPolicy, selectQuality, TIER_ORDER } from './performance/QualityPolicy.js';
import { isTouch } from './config.js';
import { applyShadowQuality } from './performance/ShadowQuality.js';
import { frameMonitor, markBoot, registerDiagnostics } from './performance/runtime.js';
const OUTLINE_COLOR = new THREE.Color(0x000000);

/**
 * The per-tier budget. Pools are ALLOCATED for the device's max tier and
 * CAPPED at the current tier, so a governor step never reallocates a buffer.
 *
 *   lights          fixed point-light pool (LightPool). Allocated at the max
 *                   tier's size so the light set — part of every lit material's
 *                   program key — never changes after boot.
 *   glowParticles   additive HDR sprite budget (sparks, cores, embers)
 *   smokeParticles  cartoon puff budget (alpha, eroding)
 *   shardParticles  angular cel chunk budget (alpha)
 *   ribbons         continuous trails in flight at once (one strip each)
 *   shadows         blob shadows under airborne things per frame
 *   coreGain        how far a white-hot core is pushed past the bloom
 *                   threshold; 1 on a tier with no bloom
 *   debris          instanced deck chunks
 *   rings / decals  shock rings and scorch marks in flight
 *   fxScale         multiplier on every recipe's particle counts
 *   footsteps       stride dust on (default) or off
 *   particleBudget  the legacy burst pool's concurrent BURSTS (still used by
 *                   the cel gathers; see ScreenEffects.js)
 */
const TIER_CONFIG = {
  ultra: { pixelRatio: 2.0, shadowSize: 2048, post: true, glow: false, outline: 'pipeline', particleBudget: 64, lights: 8, fakeLights: 0, glowParticles: 1800, smokeParticles: 900, shardParticles: 500, ribbons: 48, shadows: 96, debris: 140, rings: 12, decals: 18, fxScale: 1, coreGain: 2.4 },
  high: { pixelRatio: 1.5, shadowSize: 1024, post: true, glow: false, outline: 'pipeline', particleBudget: 48, lights: 6, fakeLights: 0, glowParticles: 1200, smokeParticles: 600, shardParticles: 360, ribbons: 40, shadows: 80, debris: 100, rings: 10, decals: 16, fxScale: 0.85, coreGain: 2.4 },
  // Medium renders at 1.0 now, not 1.25: the glow path gives it 4x MSAA and a
  // bloom, and a third fewer pixels is what pays for them (and for the two
  // real lights) on the tier that is every phone.
  medium: { pixelRatio: 1.0, shadowSize: 0, post: false, glow: true, outline: 'hull', particleBudget: 32, lights: 2, fakeLights: 4, glowParticles: 600, smokeParticles: 300, shardParticles: 200, ribbons: 28, shadows: 48, debris: 48, rings: 8, decals: 10, fxScale: 0.6, coreGain: 2.4 },
  low: { pixelRatio: 1.0, shadowSize: 0, post: false, glow: false, outline: 'hull', particleBudget: 18, lights: 0, fakeLights: 3, glowParticles: 260, smokeParticles: 130, shardParticles: 100, ribbons: 16, shadows: 32, debris: 24, rings: 6, decals: 6, fxScale: 0.4, coreGain: 1, footsteps: false },
};

/**
 * Why those light counts. Measured on this deck, idle scene, one desktop GPU:
 * every point light costs ~0.57 ms per frame at 2160x1350 (high) and ~0.34 ms
 * at 487x1055 (a phone's medium) — the per-fragment light loop on every lit
 * material, whether or not the light is bright. A phone GPU is several times
 * slower again, so the phone tiers get two real lights (medium) or none (low)
 * and the rest of their light requests are drawn as `fakeLights`: additive
 * glow sprites at the light's position, a few fragments each. See the
 * measurements in docs/design/brawl-look/PERF.md.
 */

/**
 * Bloom, on linear HDR. Threshold 2.0: a white toon body under the 2.1 key
 * peaks near 1.8, the terminal theme's candles near 1.5; the VFX kit emits at
 * 3–6. `smoothWidth` softens the knee so a spark at 2.4 blooms a little rather
 * than snapping on.
 */
const BLOOM = { strength: 0.55, radius: 0.7, threshold: 2.0, knee: 0.5 };
/**
 * Medium's bloom is three passes, not thirteen. `BloomNode` is a five-level
 * mip chain (a bright pass, five blurs each way, a composite); on the phone
 * tier the per-pass CPU setup costs more than the pixels. This one is a
 * bright pass into a quarter-resolution target and ONE separable gaussian at
 * that size. Sigma 3 at a quarter of the frame is a ~24 px halo at full size,
 * which is the whole job on a phone screen.
 */
const BLOOM_MEDIUM = { strength: 0.5, sigma: 3, threshold: 2.0, knee: 0.5, resolution: 0.25, samples: 4 };

/** The grade. Shadow tint is multiplicative and only reaches the blacks. */
const GRADE = { vignette: 0.34, saturation: 1.1, shadowTint: [0.86, 0.94, 1.06], shadowAmount: 0.45 };

/** The medium bloom's bright target, resized with the frame in renderFrame. */
let _cheapBright = null;
let _cheapBrightW = 0;
let _cheapBrightH = 0;
const _size = new THREE.Vector2();

function syncCheapBloomSize() {
  if (!_cheapBright) return;
  _renderer.getDrawingBufferSize(_size);
  const w = Math.max(2, Math.round(_size.width * BLOOM_MEDIUM.resolution));
  const h = Math.max(2, Math.round(_size.height * BLOOM_MEDIUM.resolution));
  if (w === _cheapBrightW && h === _cheapBrightH) return;
  _cheapBrightW = w;
  _cheapBrightH = h;
  _cheapBright.setSize(w, h);
}

/**
 * Profiling overrides, URL-only, for the same reason as `?tier=`: the only way
 * to know what a pass costs on a device is to switch it off on that device.
 *   ?glow=off     medium renders direct (no bloom, no grade)
 *   ?lights=N     cap the light pool at N on every tier (0 = no dynamic light)
 *   ?fxscale=X    multiply every recipe's particle counts (0 = no particles)
 */
(() => {
  const q = new URLSearchParams(window.location.search);
  if (q.get('glow') === 'off') TIER_CONFIG.medium.glow = false;
  const msaa = Number(q.get('msaa'));
  if (q.has('msaa') && Number.isFinite(msaa)) BLOOM_MEDIUM.samples = Math.max(0, msaa);
  const lights = Number(q.get('lights'));
  if (q.has('lights') && Number.isFinite(lights)) {
    for (const cfg of Object.values(TIER_CONFIG)) cfg.lights = Math.min(cfg.lights, Math.max(0, lights));
  }
  const fxscale = Number(q.get('fxscale'));
  if (q.has('fxscale') && Number.isFinite(fxscale)) {
    for (const cfg of Object.values(TIER_CONFIG)) cfg.fxScale = Math.max(0, fxscale);
  }
})();

let renderPipeline = null;
let glowPipeline = null;
/** The scene passes, kept so a prewarm can compile against their targets. */
let _scenePass = null;
let _glowScenePass = null;
let currentTier = 'medium';
let maxTier = 'high';
// `?tier=low|medium|high|ultra` pins the tier and turns the governor off —
// there is no other way to SEE what a weaker device gets without owning one.
const _tierOverride = (() => {
  const t = new URLSearchParams(window.location.search).get('tier');
  return TIER_CONFIG[t] ? t : null;
})();
let _renderer = null;
let _scene = null;
let _camera = null;
let _directionalLight = null;
const _outlineListeners = new Set();
const _tierListeners = new Set();

// Death grade: 1 = fully desaturated + edge-darkened. Lives in the pipelines
// permanently (built once, never rebuilt) and is a visual no-op at 0. On the
// direct path there is no post pass, so setDeathGrade is silently inert there —
// the death moment still reads through screen flash and shake.
const _deathGrade = uniform(0);
let _deathGradeLastDecay = 0;

/**
 * SHIFT, the market event: "the arena changes colour". A clean GRADE
 * transition — a split-tone tint and a saturation change, wiped across the
 * screen on a diagonal with a bright line on the front — rather than a raw
 * hue rotation, for two reasons. A hue rotation of a teal deck lands on
 * purple a third of the time, and v2 has no purple; and a rotation reads as
 * a broken monitor, where a grade reads as a look. The pool's `hueDeg` picks
 * one of five looks, so the director's roll still decides which.
 *
 * On the direct path (low) there is no grade pass; the canvas takes the
 * nearest CSS filter instead, the only full-frame colour op that tier has.
 */
export const SHIFT_GRADES = [
  { name: 'gold', shadow: [0.92, 0.84, 0.64], hi: [1.06, 1.0, 0.82], sat: 1.05, css: 'sepia(0.5) saturate(1.15)' },
  { name: 'acid', shadow: [0.72, 0.92, 0.66], hi: [0.9, 1.08, 0.78], sat: 1.15, css: 'sepia(0.4) hue-rotate(50deg) saturate(1.3)' },
  { name: 'cyan', shadow: [0.62, 0.88, 1.0], hi: [0.86, 1.02, 1.1], sat: 1.05, css: 'sepia(0.4) hue-rotate(160deg) saturate(1.2)' },
  { name: 'noir', shadow: [0.78, 0.82, 0.86], hi: [1.0, 1.0, 1.0], sat: 0.35, css: 'saturate(0.35)' },
  { name: 'infra', shadow: [0.95, 0.55, 0.45], hi: [1.1, 0.9, 0.78], sat: 1.2, css: 'sepia(0.5) saturate(1.7) hue-rotate(-20deg)' },
];

/** The look a `color_shift.hueDeg` selects: five 72° bins around the wheel. */
export function gradeForHue(hueDeg) {
  const h = (((Number(hueDeg) || 0) % 360) + 360) % 360;
  return SHIFT_GRADES[Math.min(SHIFT_GRADES.length - 1, Math.floor(h / (360 / SHIFT_GRADES.length)))];
}

const _shiftMix = uniform(0);
const _shiftShadow = uniform(new THREE.Color(1, 1, 1));
const _shiftHi = uniform(new THREE.Color(1, 1, 1));
const _shiftSat = uniform(1);
let _shiftCss = '';

/**
 * @param {number|null} hueDeg the mutation's hue; null keeps the last look
 * @param {number} wipe 0 = off, 1 = fully applied; in between, the front is mid-screen
 */
export function setColorShift(hueDeg, wipe = 1) {
  const grade = hueDeg == null ? null : gradeForHue(hueDeg);
  if (grade) {
    _shiftShadow.value.setRGB(...grade.shadow);
    _shiftHi.value.setRGB(...grade.hi);
    _shiftSat.value = grade.sat;
    _shiftCss = grade.css;
  }
  _shiftMix.value = Math.max(0, Math.min(1, wipe ?? 0));
  if (_renderer?.domElement) {
    const css = renderPath() === 'direct' && _shiftMix.value > 0.5 ? _shiftCss : '';
    if (_renderer.domElement.style.filter !== css) _renderer.domElement.style.filter = css;
  }
}

export function colorShiftState() {
  return { mix: _shiftMix.value, sat: _shiftSat.value, css: _shiftCss };
}

/** The grade shift on a display-referred colour, behind the wipe. */
function shiftNode(rgb) {
  const l = luminance(rgb);
  const graded = mix(vec3(l), rgb, _shiftSat).mul(mix(_shiftShadow, _shiftHi, smoothstep(float(0.08), float(0.75), l)));
  // A diagonal front from the top-left, 0..1.35 across the frame.
  const d = uv().x.add(oneMinus(uv().y).mul(0.35));
  const front = _shiftMix.mul(1.42).sub(0.04);
  const inside = oneMinus(smoothstep(front, front.add(0.05), d));
  // The bright line rides the front and only exists mid-wipe.
  const line = smoothstep(front.sub(0.03), front, d)
    .mul(oneMinus(smoothstep(front, front.add(0.02), d)))
    .mul(_shiftMix.mul(oneMinus(_shiftMix)).mul(4.0));
  return mix(rgb, graded, inside).add(vec3(line.mul(0.45)));
}

let policy = null;
let windowStart = null;
let windowFrames = 0;
let windowCpu = 0;
let shadowAllocation = 1024;

export function initialQuality() {
  return selectQuality({
    cores: navigator.hardwareConcurrency || 2,
    // The input layer's answer (touch points AND a coarse pointer, or ?touch=).
    // Touch now caps the tier, so a touchscreen laptop or a narrow desktop
    // window must not count.
    touch: isTouch,
    override: _tierOverride,
  });
}

/**
 * The grade and the death grade, on a display-referred colour.
 *
 * Saturation pulls toward or away from luminance; the shadow tint multiplies
 * in where luminance is low and fades out by mid-grey; the vignette darkens
 * toward the corners. Then the death grade (desaturate + darken the edges),
 * which is 0 in normal play.
 */
function gradeNode(colorNode) {
  const rgb = colorNode.rgb;
  const lum = luminance(rgb);
  let out = mix(vec3(lum), rgb, float(GRADE.saturation));
  const shadow = float(1).sub(smoothstep(float(0.0), float(0.5), lum));
  const tinted = out.mul(vec3(...GRADE.shadowTint));
  out = mix(out, tinted, shadow.mul(shadow).mul(GRADE.shadowAmount));
  const edge = uv().sub(0.5).length();
  const vignette = float(1).sub(smoothstep(float(0.32), float(0.86), edge).mul(GRADE.vignette));
  out = out.mul(vignette);
  // The market's SHIFT, a no-op at mix 0.
  out = shiftNode(out);
  // Death: pull toward dimmed luminance, darken toward the corners.
  const desat = mix(out, vec3(luminance(out)).mul(0.8), _deathGrade.mul(0.85));
  const deathVignette = float(1).sub(smoothstep(float(0.3), float(0.72), edge).mul(_deathGrade).mul(0.5));
  return vec4(desat.mul(deathVignette), colorNode.a);
}

// Build the full effects pipeline once — never rebuild at runtime.
// Rebuilding disposes GPU textures (shadow maps, render targets) while the GPU
// may still reference them, causing "Destroyed texture" crashes.
function buildPipelineOnce() {
  renderPipeline = new THREE.RenderPipeline(_renderer);

  // FXAANode requires sRGB input — "tone mapping and color space conversion
  // must happen before the anti-aliasing" (r183 FXAANode docs). The pipeline's
  // default output transform appends them AFTER our chain, which left the AA
  // working on linear HDR and barely touching the black toon outlines this art
  // style lives on. So: disable the automatic transform and place
  // renderOutput() mid-chain ourselves.
  renderPipeline.outputColorTransform = false;

  // 0.006, not v1's 0.003: the thickness is clip-space (screen-proportional),
  // so 0.003 is ~1px at 1080p and FXAA erases it — v1 shipped an outline nobody
  // could see. 0.006 ≈ 0.3% of screen height, which is also the effective weight
  // CheapOutline.js produces on the hull tiers, so the silhouette no longer
  // changes weight when the governor crosses the pipeline/hull boundary.
  const scenePass = toonOutlinePass(_scene, _camera, OUTLINE_COLOR, 0.006, 1.0);
  _scenePass = scenePass;
  const scenePassColor = scenePass.getTextureNode('output');
  // Bloom stays in linear HDR — thresholding after tone mapping would crush it.
  const bloomNode = new BloomNode(scenePassColor, BLOOM.strength, BLOOM.radius, BLOOM.threshold);
  bloomNode.smoothWidth.value = BLOOM.knee;
  let outputNode = scenePassColor.add(bloomNode);
  // ACES + sRGB here (both resolved from the renderer via pipeline context)…
  outputNode = renderOutput(outputNode);
  // …so FXAA runs on display-referred sRGB, per its contract.
  outputNode = fxaa(outputNode);
  // The grade last: a per-pixel colour transform does not disturb FXAA's
  // edge detection, and the vignette must never be anti-aliased into the edges.
  outputNode = gradeNode(outputNode);

  renderPipeline.outputNode = outputNode;
  renderPipeline.needsUpdate = true;
}

/**
 * Medium's chain: scene, quarter-frame bloom, tone map, grade. No outline pass
 * (that is a second scene render), no FXAA (the MSAA'd scene pass covers the
 * geometry edges and nothing else on this path has edges worth the pass).
 */
function buildGlowPipelineOnce() {
  glowPipeline = new THREE.RenderPipeline(_renderer);
  glowPipeline.outputColorTransform = false;
  // MSAA on the scene target: the direct path this replaces was MSAA'd by the
  // canvas, and a phone without it shows every deck edge as a staircase.
  const scenePass = pass(_scene, _camera, { samples: BLOOM_MEDIUM.samples });
  _glowScenePass = scenePass;
  const scenePassColor = scenePass.getTextureNode('output');
  // Bright pass: the HDR excess above the threshold, into a quarter-size
  // target (fixed size; renderFrame resizes it with the drawing buffer).
  const lum = luminance(scenePassColor.rgb);
  const knee = smoothstep(float(BLOOM_MEDIUM.threshold), float(BLOOM_MEDIUM.threshold + BLOOM_MEDIUM.knee), lum);
  _cheapBright = rtt(vec4(scenePassColor.rgb.mul(knee), 1.0), 2, 2);
  _cheapBright.setPixelRatio(1);
  // One separable blur at the bright target's own size: two passes.
  const halo = gaussianBlur(_cheapBright, null, BLOOM_MEDIUM.sigma);
  let outputNode = scenePassColor.add(halo.mul(BLOOM_MEDIUM.strength));
  outputNode = renderOutput(outputNode);
  outputNode = gradeNode(outputNode);
  glowPipeline.outputNode = outputNode;
  glowPipeline.needsUpdate = true;
}

export function initPostProcessing(rendererRef, sceneRef, cameraRef, directionalLight, quality = initialQuality()) {
  _renderer = rendererRef;
  _scene = sceneRef;
  _camera = cameraRef;
  _directionalLight = directionalLight || null;

  currentTier = quality.tier;
  maxTier = quality.maxTier;
  shadowAllocation = quality.shadowSize;
  policy = new QualityPolicy(quality);
  // Allocation dimensions are final before any render. High never inherits
  // the old unconditional 2048 allocation.
  _directionalLight?.shadow.mapSize.set(shadowAllocation, shadowAllocation);
  document.addEventListener('visibilitychange', () => {
    windowStart = null;
    windowFrames = windowCpu = 0;
    policy.observe({ now: performance.now(), visible: false });
  });
  registerDiagnostics('rendering', () => ({
    backend: _renderer.backend?.isWebGPUBackend ? 'webgpu' : 'webgl2',
    // `maxTier` is what the pools were sized for; `ceiling` is where the
    // governor may still climb, lowered after a tier fails twice.
    tier: currentTier, maxTier, ceiling: policy?.maxTier ?? maxTier, shadowAllocation, shadows: !!_directionalLight?.shadow.autoUpdate,
    renderPath: renderPath(),
    bloom: renderPath() === 'pipeline' ? BLOOM : renderPath() === 'glow' ? BLOOM_MEDIUM : null,
    budget: TIER_CONFIG[currentTier],
    pixelRatio: _renderer.getPixelRatio(), capFps: policy.capFps || null,
    gpuTimestampSupported: _renderer.hasFeature?.('timestamp-query') ?? false,
    gpuTimingEnabled: false,
    hardware: { cores: navigator.hardwareConcurrency ?? null, dpr: window.devicePixelRatio, width: window.innerWidth, height: window.innerHeight },
  }));

  // Built once, at boot, if this device could ever reach a tier that uses it.
  // Deferring it would mean building at runtime, which is the crash above.
  if (TIER_ORDER.indexOf(maxTier) >= TIER_ORDER.indexOf('high')) {
    buildPipelineOnce();
  }
  // Same rule, same reason. A device capped at `low` never reaches medium, so
  // it never pays for the glow chain either.
  if (TIER_ORDER.indexOf(maxTier) >= TIER_ORDER.indexOf('medium')) {
    buildGlowPipelineOnce();
  }
  _renderer.setPixelRatio(Math.min(window.devicePixelRatio, TIER_CONFIG[currentTier].pixelRatio));
  applyShadowSettings(currentTier);

  console.log(`[PostProcess] quality=${currentTier} max=${maxTier} outline=${outlineMode()} path=${renderPath()}`);
}

/**
 * The render target the scene is drawn into on the current path, or null for
 * the canvas. A prewarm must compile against THIS: on WebGPU the pipeline is
 * keyed on the target's sample count and colour format, and the post paths
 * draw the scene into a HalfFloat target, not the canvas.
 */
export function scenePassTarget() {
  const path = renderPath();
  if (path === 'pipeline') return _scenePass?.renderTarget ?? null;
  if (path === 'glow') return _glowScenePass?.renderTarget ?? null;
  return null;
}

/** Which chain a frame takes right now: 'pipeline' | 'glow' | 'direct'. */
export function renderPath() {
  const cfg = TIER_CONFIG[currentTier];
  if (renderPipeline && cfg.post) return 'pipeline';
  if (glowPipeline && cfg.glow) return 'glow';
  return 'direct';
}

export function renderFrame(frameStart = performance.now(), stages = {}) {
  const renderStart = performance.now();
  const path = renderPath();
  if (path === 'pipeline') renderPipeline.render();
  else if (path === 'glow') {
    syncCheapBloomSize();
    glowPipeline.render();
  } else _renderer.render(_scene, _camera);
  const now = performance.now();
  const info = _renderer.info;
  frameMonitor.record({ start: frameStart, renderStart, end: now, stages,
    counters: {
      calls: info.render?.drawCalls ?? info.render?.calls ?? null,
      triangles: info.render?.triangles ?? null,
      geometries: info.memory?.geometries ?? null,
      textures: info.memory?.textures ?? null,
    },
  });
  markBoot('firstRenderedFrame');

  if (_deathGrade.value > 0) {
    const dt = _deathGradeLastDecay > 0 ? (now - _deathGradeLastDecay) / 1000 : 0;
    _deathGrade.value = Math.max(0, _deathGrade.value - dt / 0.45);
    _deathGradeLastDecay = _deathGrade.value > 0 ? now : 0;
  }
  if (document.hidden) return;
  if (windowStart == null) { windowStart = frameStart; return; }
  windowFrames++;
  windowCpu += now - frameStart;
  if (frameStart - windowStart >= 1000) {
    const tier = policy.observe({ now: frameStart, fps: windowFrames * 1000 / (frameStart - windowStart), cpuMs: windowCpu / windowFrames });
    applyTier(tier);
    windowStart = frameStart;
    windowFrames = windowCpu = 0;
  }
}

function applyTier(tier) {
  if (tier === currentTier) return;
  const before = outlineMode();
  currentTier = tier;
  console.log(`[PostProcess] quality=${tier} path=${renderPath()}`);

  _renderer.setPixelRatio(Math.min(window.devicePixelRatio, TIER_CONFIG[tier].pixelRatio));
  applyShadowSettings(tier);

  // Crossing the pipeline/hull boundary changes who draws the silhouette. Hulls
  // are added and removed per object, so the listener does that work rather than
  // this module reaching into the scene graph.
  const after = outlineMode();
  if (after !== before) {
    for (const fn of _outlineListeners) fn(after);
  }
  for (const fn of _tierListeners) fn(TIER_CONFIG[tier], tier);
}

function applyShadowSettings(tier) {
  applyShadowQuality(_directionalLight, TIER_CONFIG[tier].shadowSize > 0);
}

/**
 * Death desaturation moment. Call with 1 on elimination; it ramps back to 0 on
 * its own over ~450ms. No-op on the direct path.
 */
export function setDeathGrade(t) {
  _deathGrade.value = Math.max(0, Math.min(1, t ?? 0));
  _deathGradeLastDecay = _deathGrade.value > 0 ? performance.now() : 0;
}

export function getParticleBudget() {
  return TIER_CONFIG[currentTier].particleBudget;
}

export function getCurrentTier() {
  return currentTier;
}

/** The current tier's budget row. */
export function getTierConfig() {
  return TIER_CONFIG[currentTier];
}

/** The device's ceiling — what the pools are allocated for. */
export function getMaxTierConfig() {
  return TIER_CONFIG[maxTier];
}

/** Every tier row, for tests and the bench. Frozen: budgets are authored here. */
export function tierConfigs() {
  return TIER_CONFIG;
}

/** 'pipeline' when the post pass draws outlines, 'hull' when the meshes must. */
export function outlineMode() {
  return TIER_CONFIG[currentTier].outline;
}

/** Called with the new mode whenever the outline owner changes. */
export function onOutlineModeChange(fn) {
  _outlineListeners.add(fn);
  return () => _outlineListeners.delete(fn);
}

/** Called with (config, tier) whenever the governor moves. */
export function onTierChange(fn) {
  _tierListeners.add(fn);
  return () => _tierListeners.delete(fn);
}

export function setMaxTier(tier) {
  // Ultra requires the explicit startup override: changing allocation after
  // rendering would invalidate in-flight shadow textures.
  if (TIER_CONFIG[tier] && (tier !== 'ultra' || shadowAllocation === 2048)) {
    maxTier = tier;
    if (policy) policy.maxTier = tier;
  }
}
